import * as real from './api.real.js';
import { fixtureMode } from './data-mode.js';

/**
 * The ONE data boundary. Pages import from here and never learn which adapter answered.
 * `api.real.ts` is the unchanged production adapter; `dev-fixtures/adapter.ts` is a
 * read-only stand-in that exists only for local design QA (see lib/data-mode.ts).
 */
export type { ApiError, ImportResult, PlacesError, PortalLink, PreferencesError, ReviewItem, ReviewQueue } from './api.real.js';
export { reviewItemSchema, reviewQueueSchema } from './api.real.js';

type Real = typeof real;
export type DataAdapter = Pick<
  Real,
  | 'fetchHealth'
  | 'fetchPeople'
  | 'fetchPerson'
  | 'fetchControlledForms'
  | 'issueControlledForm'
  | 'fetchPersonHistory'
  | 'appendPersonHistory'
  | 'fetchChecklist'
  | 'fetchOnboardingPreview'
  | 'fetchCatalogOptions'
  | 'fetchAgencyInterviewers'
  | 'changeAgencyInterviewers'
  | 'fetchStepAnswers'
  | 'fetchPersonSteps'
  | 'fetchSensitiveOnFile'
  | 'revealSensitive'
  | 'updateSensitive'
  | 'fetchDashboard'
  | 'fetchOffers'
  | 'fetchInterviews'
  | 'fetchPortalLinks'
  | 'createPerson'
  | 'createStaffMember'
  | 'changeLifecycle'
  | 'updatePerson'
  | 'issuePortalLink'
  | 'fetchPortalLinkStatus'
  | 'emailPortalLink'
  | 'revokePortalLink'
  | 'importPeople'
  | 'fetchReviewQueue'
  | 'approveSubmission'
  | 'rejectSubmission'
  | 'recordRequirement'
  | 'fetchPreferences'
  | 'setLastFilters'
  | 'createSavedFilter'
  | 'renameSavedFilter'
  | 'deleteSavedFilter'
  | 'deleteUnreadableSavedFilters'
  | 'searchPlaces'
  | 'resolvePlace'
  | 'fetchPlaceNames'
  | 'fetchCoverageMap'
>;

/**
 * The fixture adapter is reached only through this guarded dynamic import. In a production
 * build `fixtureMode` is the literal `false`, so the branch is dead code and the fixture
 * dataset, adapter and QA bar are never emitted — scripts/check-production-bundle.mjs proves
 * it on the built output. Resolved per call, so a test can flip the mode; a test that has
 * already imported the adapter module shares the same instance.
 */
let fixtures: DataAdapter | null = null;
async function adapter(): Promise<DataAdapter> {
  if (!fixtureMode) return real;
  // Never fall through to the real API in fixture mode: that would be exactly the silent switch this boundary forbids.
  if (fixtures === null) {
    const module = await import('../../dev-fixtures/adapter.js');
    fixtures = module.fixtureAdapter;
    // The fixture's own "today" arrives with its data, through the same guarded import.
    installedClock = module.clock;
  }
  return fixtures;
}

/**
 * The clock the UI reads for anything relative ("3 days ago", "waiting 4 days"). Production
 * reads the real time; fixture mode reads the fixture's own "today" (checkpoint 7C decision 3),
 * so fictional ages never drift with the machine's date. Same boundary as the data, for the
 * same reason — and reached the same way: the fixture clock is installed only when the fixture
 * adapter loads through the guarded import above, so a production build never contains it.
 */
export interface Clock {
  readonly now: () => number;
}
const realClock: Clock = { now: () => Date.now() };
let installedClock: Clock | null = null;
// Resolved per call, like the adapter, so a test can flip the mode; in a build `fixtureMode` is a constant.
export const now = (): number => (fixtureMode && installedClock !== null ? installedClock : realClock).now();

export const fetchHealth: Real['fetchHealth'] = (...a) => adapter().then((d) => d.fetchHealth(...a));
export const fetchPeople: Real['fetchPeople'] = (...a) => adapter().then((d) => d.fetchPeople(...a));
export const fetchPerson: Real['fetchPerson'] = (...a) => adapter().then((d) => d.fetchPerson(...a));
export const fetchPersonHistory: Real['fetchPersonHistory'] = (...a) => adapter().then((d) => d.fetchPersonHistory(...a));
export const appendPersonHistory: Real['appendPersonHistory'] = (...a) => adapter().then((d) => d.appendPersonHistory(...a));
export const fetchChecklist: Real['fetchChecklist'] = (...a) => adapter().then((d) => d.fetchChecklist(...a));
export const fetchOnboardingPreview: Real['fetchOnboardingPreview'] = (...a) => adapter().then((d) => d.fetchOnboardingPreview(...a));
export const fetchCatalogOptions: Real['fetchCatalogOptions'] = (...a) => adapter().then((d) => d.fetchCatalogOptions(...a));
export const fetchAgencyInterviewers: Real['fetchAgencyInterviewers'] = (...a) => adapter().then((d) => d.fetchAgencyInterviewers(...a));
export const changeAgencyInterviewers: Real['changeAgencyInterviewers'] = (...a) => adapter().then((d) => d.changeAgencyInterviewers(...a));
export const fetchPersonSteps: Real['fetchPersonSteps'] = (...a) => adapter().then((d) => d.fetchPersonSteps(...a));
export const fetchStepAnswers: Real['fetchStepAnswers'] = (...a) => adapter().then((d) => d.fetchStepAnswers(...a));
export const fetchSensitiveOnFile: Real['fetchSensitiveOnFile'] = (...a) => adapter().then((d) => d.fetchSensitiveOnFile(...a));
export const revealSensitive: Real['revealSensitive'] = (...a) => adapter().then((d) => d.revealSensitive(...a));
export const updateSensitive: Real['updateSensitive'] = (...a) => adapter().then((d) => d.updateSensitive(...a));
export const fetchDashboard: Real['fetchDashboard'] = (...a) => adapter().then((d) => d.fetchDashboard(...a));
export const fetchOffers: Real['fetchOffers'] = (...a) => adapter().then((d) => d.fetchOffers(...a));
export const fetchInterviews: Real['fetchInterviews'] = (...a) => adapter().then((d) => d.fetchInterviews(...a));
export const fetchPortalLinks: Real['fetchPortalLinks'] = (...a) => adapter().then((d) => d.fetchPortalLinks(...a));
export const createPerson: Real['createPerson'] = (...a) => adapter().then((d) => d.createPerson(...a));
export const createStaffMember: Real['createStaffMember'] = (...a) => adapter().then((d) => d.createStaffMember(...a));
export const changeLifecycle: Real['changeLifecycle'] = (...a) => adapter().then((d) => d.changeLifecycle(...a));
export const updatePerson: Real['updatePerson'] = (...a) => adapter().then((d) => d.updatePerson(...a));
export const issuePortalLink: Real['issuePortalLink'] = (...a) => adapter().then((d) => d.issuePortalLink(...a));
export const fetchPortalLinkStatus: Real['fetchPortalLinkStatus'] = (...a) => adapter().then((d) => d.fetchPortalLinkStatus(...a));
export const emailPortalLink: Real['emailPortalLink'] = (...a) => adapter().then((d) => d.emailPortalLink(...a));
export const revokePortalLink: Real['revokePortalLink'] = (...a) => adapter().then((d) => d.revokePortalLink(...a));
export const importPeople: Real['importPeople'] = (...a) => adapter().then((d) => d.importPeople(...a));
export const fetchReviewQueue: Real['fetchReviewQueue'] = (...a) => adapter().then((d) => d.fetchReviewQueue(...a));
export const approveSubmission: Real['approveSubmission'] = (...a) => adapter().then((d) => d.approveSubmission(...a));
export const rejectSubmission: Real['rejectSubmission'] = (...a) => adapter().then((d) => d.rejectSubmission(...a));
export const recordRequirement: Real['recordRequirement'] = (...a) => adapter().then((d) => d.recordRequirement(...a));
export const fetchPreferences: Real['fetchPreferences'] = (...a) => adapter().then((d) => d.fetchPreferences(...a));
export const setLastFilters: Real['setLastFilters'] = (...a) => adapter().then((d) => d.setLastFilters(...a));
export const createSavedFilter: Real['createSavedFilter'] = (...a) => adapter().then((d) => d.createSavedFilter(...a));
export const renameSavedFilter: Real['renameSavedFilter'] = (...a) => adapter().then((d) => d.renameSavedFilter(...a));
export const deleteSavedFilter: Real['deleteSavedFilter'] = (...a) => adapter().then((d) => d.deleteSavedFilter(...a));
export const deleteUnreadableSavedFilters: Real['deleteUnreadableSavedFilters'] = (...a) => adapter().then((d) => d.deleteUnreadableSavedFilters(...a));
export const searchPlaces: Real['searchPlaces'] = (...a) => adapter().then((d) => d.searchPlaces(...a));
export const resolvePlace: Real['resolvePlace'] = (...a) => adapter().then((d) => d.resolvePlace(...a));
export const fetchPlaceNames: Real['fetchPlaceNames'] = (...a) => adapter().then((d) => d.fetchPlaceNames(...a));
export const fetchCoverageMap: Real['fetchCoverageMap'] = (...a) => adapter().then((d) => d.fetchCoverageMap(...a));

export const fetchControlledForms: Real['fetchControlledForms'] = (...a) => adapter().then((d) => d.fetchControlledForms(...a));
export const issueControlledForm: Real['issueControlledForm'] = (...a) => adapter().then((d) => d.issueControlledForm(...a));
